import React, { useState, useRef } from 'react'
import styled, { keyframes } from 'styled-components/macro'
import { ColorsMap, device } from '@/utils/Theme'
import { Button } from '@/components/Button'
import Icons from '@/components/Icons'

import { space, layout } from 'styled-system'

const SALE_IMAGES = [
  '/images/banners/hummel_sale_2026_1.jpg',
  '/images/banners/hummel_sale_2026_2.jpg',
  '/images/banners/hummel_sale_2026_3.jpg',
  '/images/banners/hummel_sale_2026_4.jpg',
  '/images/banners/hummel_sale_2026_5.jpg',
  '/images/banners/hummel_sale_2026_6.jpg',
]

const Sale2026 = () => {
  const [current, setCurrent] = useState(0)
  const touchStartX = useRef<number | null>(null)

  const prev = () => setCurrent((c) => (c === 0 ? SALE_IMAGES.length - 1 : c - 1))
  const next = () => setCurrent((c) => (c === SALE_IMAGES.length - 1 ? 0 : c + 1))

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const delta = touchStartX.current - e.changedTouches[0].clientX
    if (delta > 40) next()
    else if (delta < -40) prev()
    touchStartX.current = null
  }

  return (
    <StyledSectionWrapper>
      <Inner>
        {/* ── LEFT: TEXT ── */}
        <TextSide>
          <Headline>
            AKCIA&nbsp;ROKA
            <br />
            JE&nbsp;TU!
          </Headline>
          <Body>
            Vybavte svoj tím kvalitnými dresmi od&nbsp;<strong>HUMMEL</strong> za výnimočných podmienok. Pri nákupe
            minimálne <Highlight>15 sád dresov a treniek</Highlight> HUMMEL získate druhú sadu za{' '}
            <Highlight>polovicu ceny</Highlight>.
          </Body>
          <Body>
            Ponuka je ideálna pre kluby, akadémie aj amatérske tímy, ktoré sa chcú pripraviť na sezónu výhodne a v
            jednotnom štýle. Zľava sa uplatňuje na lacnejšiu sadu.
          </Body>
          <Pill>Akcia platí do 31.&nbsp;12.&nbsp;2026</Pill>
        </TextSide>

        {/* ── RIGHT: CAROUSEL ── */}
        <CarouselSide onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <SliderTrack $current={current} $total={SALE_IMAGES.length}>
            {SALE_IMAGES.map((src, i) => (
              <Slide key={i}>
                <SlideImage src={src} alt={`Hummel sale 2026 – ${i + 1}`} />
              </Slide>
            ))}
          </SliderTrack>

          {/* Overlaid arrow buttons — same pattern as ProductArea */}
          <ButtonWrapperLeft onClick={prev}>
            <Button variant="verticalRad" colorVariant="primaryWhite" borderNone height="100%">
              <Icons.ChevronLeft />
            </Button>
          </ButtonWrapperLeft>

          <ButtonWrapperRight onClick={next}>
            <Button variant="verticalRad" colorVariant="primaryWhite" borderNone height="100%">
              <Icons.ChevronRight />
            </Button>
          </ButtonWrapperRight>

          {/* Dots */}
          <Dots>
            {SALE_IMAGES.map((_, i) => (
              <Dot key={i} $active={i === current} onClick={() => setCurrent(i)} />
            ))}
          </Dots>
        </CarouselSide>
      </Inner>
    </StyledSectionWrapper>
  )
}

export default Sale2026

/* ─── Styled Components ──────────────────────────────────────────────── */

export const ButtonWrapperLeft = styled.div`
  position: absolute;
  top: 50%;
  left: 1.2%;
  right: auto; /* explicitly set */
  bottom: auto; /* explicitly set */
  transform: translate(0, -50%);
  height: 25%;
  z-index: 10;
  ${space}
`

export const ButtonWrapperRight = styled.div`
  position: absolute;
  top: 50%;
  left: auto; /* explicitly set */
  right: 1.2%;
  bottom: auto; /* explicitly set */
  transform: translate(0, -50%);
  height: 25%;

  z-index: 10;
  ${space}
`

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`

const StyledSectionWrapper = styled.div`
  padding: 3.5rem 13%;

  @media (max-width: ${device.mobileS}) {
    padding: 3.5rem 1rem;
  }
`

const Inner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: ${device.tablet}) {
    grid-template-columns: 1fr;
  }
`

/* ── Left ── */
const TextSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  color: ${ColorsMap.black};
`

const Badge = styled.span`
  display: inline-block;
  background: #000;
  color: ${ColorsMap.primary};
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  padding: 0.25rem 0.75rem;
  border-radius: 2px;
  width: fit-content;
  text-transform: uppercase;
`

const Headline = styled.h2`
  font-size: clamp(4.2rem, 5vw, 3.5rem);
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: -0.02em;
  margin: 0;
  text-transform: uppercase;
`

const Body = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  margin: 0;
  opacity: 0.92;
`

const Highlight = styled.strong`
  color: ${ColorsMap.primary};
  border-bottom: 2px solid ${ColorsMap.primary};
  padding-bottom: 1px;
`

const Pill = styled.div`
  display: inline-block;
  border: 2px solid ${ColorsMap.black};
  border-radius: 999px;
  padding: 0.5rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  width: fit-content;
  animation: ${pulse} 3s ease-in-out infinite;
`

/* ── Right / Carousel ── */
const CarouselSide = styled.div`
  position: relative;
  overflow: hidden;
`

const SliderTrack = styled.div<{ $current: number; $total: number }>`
  display: flex;
  width: ${({ $total }) => $total * 100}%;
  transform: translateX(${({ $current, $total }) => -($current / $total) * 100}%);
  transition: transform 0.45s cubic-bezier(0.77, 0, 0.18, 1);
`

const Slide = styled.div`
  flex: 1;
  min-width: 0;
`

const SlideImage = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  display: block;
`

/* Arrow buttons — absolutely positioned inside the carousel, same as ProductArea */

const Dots = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  align-items: center;
  z-index: 10;
`

const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '1.5rem' : '0.5rem')};
  height: 0.5rem;
  border-radius: 999px;
  border: none;
  background: ${({ $active }) => ($active ? '#fff' : 'rgba(255,255,255,0.5)')};
  cursor: pointer;
  padding: 0;
  transition: width 0.3s, background 0.3s;
`

